import {noop} from '@benchling/js-utils';
import {Box, Group, SegmentedControl, Text} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  ExperimentalInputsComponentName,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {useInputTable} from 'procedures/planning/useInputTable';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';

import NoParametersWarningBanner from '../common/NoParametersWarningBanner';
import {ExperimentFlowchart} from '../design-state/ExperimentalInputs';
import useFilteredInputTableDoc, {ExperimentalInputsFilterMode} from './useFilteredInputTableDoc';

interface ProcedureRunDesignEditorRightSidepanelProps {
  templateFlowchart: ExperimentFlowchart;
  selectedMethodId: string;
  procedureType: ProcedureType;
  context: ExperimentalInputsContextProperties;
  isProcedureRunPlan: boolean;
}

function isExperimentalInputsFilterMode(value: string): value is ExperimentalInputsFilterMode {
  return value === ExperimentalInputsFilterMode.ALL || value === ExperimentalInputsFilterMode.VARYING;
}

export default function ProcedureRunDesignEditorRightSidepanel(
  props: ProcedureRunDesignEditorRightSidepanelProps
): JSX.Element {
  const {context} = props;
  const selectedMethod = props.templateFlowchart.nodes.find((m) => m.id === props.selectedMethodId);
  assert(selectedMethod != null, 'Selected method should not be null');
  const [inputTableFilterMode, setInputTableFilterMode] = useState<ExperimentalInputsFilterMode>(
    ExperimentalInputsFilterMode.ALL
  );
  const {filteredTableViewByComponent, allCount, variesCount} = useFilteredInputTableDoc({
    context,
    selectedMethod,
    templateFlowchart: props.templateFlowchart,
    procedureType: props.procedureType,
    inputTableFilterMode,
    isProcedureRunPlan: props.isProcedureRunPlan,
  });

  useEffect(() => {
    if (inputTableFilterMode === ExperimentalInputsFilterMode.VARYING && variesCount === 0) {
      setInputTableFilterMode(ExperimentalInputsFilterMode.ALL);
    }
  }, [inputTableFilterMode, variesCount]);

  const shouldRenderFilterToggle = allCount > 0;
  const handleFilterModeChange = (value: string): void => {
    assert(isExperimentalInputsFilterMode(value), `Unexpected input table filter mode: ${value}`);
    setInputTableFilterMode(value);
  };
  const options = {context, selectedMethod, locked: true, procedureType: props.procedureType};
  const parameterTable = useInputTable(ExperimentalInputsComponentName.PARAMETERS, {
    ...options,
    filteredTableView: filteredTableViewByComponent?.[ExperimentalInputsComponentName.PARAMETERS],
  });
  const materialInputsTable = useInputTable(ExperimentalInputsComponentName.MATERIAL_INPUTS, {
    ...options,
    filteredTableView: filteredTableViewByComponent?.[ExperimentalInputsComponentName.MATERIAL_INPUTS],
  });
  const equipmentTable = useInputTable(ExperimentalInputsComponentName.EQUIPMENT, {
    ...options,
    filteredTableView: filteredTableViewByComponent?.[ExperimentalInputsComponentName.EQUIPMENT],
  });
  const conditionsText = getProcedureRunConditionTextForProcedureType(props.procedureType, {plural: true});

  const shouldRenderAnyTable = parameterTable || materialInputsTable || equipmentTable;

  const value = useMemo(
    () => ({
      procedureValidationErrors: Object.entries(context.evaluatedValues).flatMap(([methodId, values]) =>
        values.blockingValueErrorCount > 0
          ? [
              {
                errorMessage: `Some ${conditionsText} have invalid values specified`,
                type: ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE,
                primaryObject: {id: methodId, objectType: ProcedureValidationObjectType.METHOD},
                parentObjects: [],
              } satisfies ProcedureValidationError,
            ]
          : []
      ),
      anyTabValidationErrors: false,
      setAnyTabValidationErrors: noop,
    }),
    [conditionsText, context.evaluatedValues]
  );

  return (
    <ProcedureValidationContext.Provider value={value}>
      <Box p="lg" {...getTestProps({component: 'ProcedureRunDesignEditorRightSidepanel', element: 'root'})}>
        {shouldRenderAnyTable ? (
          <>
            {shouldRenderFilterToggle && (
              <Group mb="md" spacing="xs">
                <Text size="sm">Show:</Text>
                <SegmentedControl
                  aria-label="Filter factors shown"
                  data={[
                    {label: `All (${allCount})`, value: ExperimentalInputsFilterMode.ALL},
                    {
                      label: `Varies (${variesCount})`,
                      value: ExperimentalInputsFilterMode.VARYING,
                      disabled: variesCount === 0,
                    },
                  ]}
                  onChange={handleFilterModeChange}
                  size="xs"
                  value={inputTableFilterMode}
                  {...getTestProps({
                    component: 'ProcedureRunDesignEditorRightSidepanel',
                    element: 'factorFilterToggle',
                  })}
                />
              </Group>
            )}
            {materialInputsTable}
            {parameterTable}
            {equipmentTable}
          </>
        ) : (
          <NoParametersWarningBanner procedureType={props.procedureType} conditionsText={conditionsText} />
        )}
      </Box>
    </ProcedureValidationContext.Provider>
  );
}
